/* global PiniaSetupStoreHelpers */
import type { UnwrapRef } from 'vue';
import type { SubmitEventPromise } from 'vuetify';
import type { ReadonlyDeep, Writable } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type {
  InitialData as NewMessageChatDialogFormInitialData,
  ValidData as NewMessageChatDialogFormValidData,
} from '@/components/dialogForms/NewMessageChatDialogForm.vue';
import type {
  InitialData as ChatMainSendFormInitialData,
  ValidData as ChatMainSendFormValidData,
} from '@/components/forms/ChatMainSendForm.vue';
import type {
  InitialData as ChatRosterSideBarFormInitialData,
  ValidData as ChatRosterSideBarFormValidData,
} from '@/components/forms/ChatRosterSideBarForm.vue';
import type { SmsChatDetailsSectionProps } from '@/components/sections/SmsChatDetailsSection.vue';
import type { SmsChatMessageFullUnitProps } from '@/components/units/SmsChatMessageFullUnit.vue';
import type { SmsChatMessagePreviewUnitProps } from '@/components/units/SmsChatMessagePreviewUnit.vue';
import type { SmsChatViewDataComposable } from '@/composables/data/useSmsChatViewData';
import type {
  DefRouteQuery as SmsDefRouteQuery,
  RouteQueryComposable as SmsRouteQueryComposable,
} from '@/composables/routeQueries/useSmsRouteQuery';
import type { DefDialogState, DialogName } from '@/stores/useDialogStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, reactive, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';

import { useDialogStore } from '@/stores/useDialogStore';
import { useStaticStore } from '@/stores/useStaticStore';
// import * as locationQueryHelpers from '@/helpers/locationQuery';
// import * as commonConstants from '@/constants/common';
import * as routeNames from '@/constants/routeNames';

interface DefPrivateSmsChatViewState {
  data: RequiredKeys<Partial<Writable<UnwrapRef<SmsChatViewDataComposable>>>>;
  query: RequiredKeys<SmsDefRouteQuery>;
}

interface DefSmsChatViewState {
  search: string | null | undefined;
  conversations:
    | ReadonlyDeep<openapi.GetSmsMessagesConversations_200['results']>
    | undefined;
}

const defPrivateSmsChatViewState: DefPrivateSmsChatViewState = {
  data: {
    isFetchingConversationsData: undefined,
    isFetchingMessageHistoryData: undefined,
    isFetchingMarkReadData: undefined,
    isFetchingSendData: undefined,
    conversationsData: undefined,
    messageHistoryData: undefined,
    markReadData: undefined,
    sendData: undefined,
    conversationsDataError: undefined,
    messageHistoryDataError: undefined,
    markReadDataError: undefined,
    sendDataError: undefined,
    refreshConversationsData: undefined,
    refreshMessageHistoryData: undefined,
    refreshMarkReadData: undefined,
    refreshSendData: undefined,
    reload: undefined,
  },
  query: {
    recipient: undefined,
  },
};

const defSmsChatViewState: DefSmsChatViewState = {
  search: undefined,
  conversations: undefined,
};

function privateSmsChatViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defPrivateSmsChatViewState));
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const route = useRoute() || {}; // TODO mock useRoute

  async function $reset(): Promise<void> {
    const reload = state.data.reload;
    const query = state.query;
    Object.assign(state, _cloneDeep(defPrivateSmsChatViewState));
    if (route.name === routeNames.SMS_CHAT) {
      state.query = query; // preserve query while on the page
      if (reload) {
        setData(reload()); // reload data while on the page
      }
    }
  }

  function setData(viewData: SmsChatViewDataComposable) {
    state.data = reactive(viewData);
  }

  function setQuery(routeQuery: SmsRouteQueryComposable) {
    state.query = reactive(routeQuery);
  }

  watch(
    () => route.name,
    function () {
      if (route.name !== routeNames.SMS_CHAT) {
        void $reset(); // reset when leave the page
      }
    },
    { immediate: true },
  );

  return {
    ...toRefs(state),
    $reset,
    setData,
    setQuery,
  };
}

function smsChatViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defSmsChatViewState));
  // const route = useRoute();
  // const router = useRouter();

  const staticStore = useStaticStore();
  const dialogStore = useDialogStore();

  const _smsChatViewStore = usePrivateSmsChatViewStore();

  const data = computed(function () {
    return _smsChatViewStore.data;
  });

  const query = computed(function () {
    return _smsChatViewStore.query;
  });

  const newMessageChatDialogFormInitialData =
    computed<NewMessageChatDialogFormInitialData>(() => {
      const initialData: RequiredKeys<NewMessageChatDialogFormInitialData> = {
        receiverType: undefined,
        driver: undefined,
        offsider: undefined,
        phone: undefined,
        extraPhone: undefined,
        message: undefined,
      };
      return initialData;
    });

  const chatMainSendFormInitialData = ref<
    RequiredKeys<ChatMainSendFormInitialData>
  >({
    message: undefined,
  });

  const chatRosterSideBarFormInitialData =
    computed<ChatRosterSideBarFormInitialData>(() => {
      const initialData: RequiredKeys<ChatRosterSideBarFormInitialData> = {
        search: undefined,
      };
      return initialData;
    });

  const messagePreviews = computed<SmsChatMessagePreviewUnitProps[]>(() => {
    if (!state.conversations) return [];
    return state.conversations.map((item) => {
      let id = item.phone_number;
      // TODO phone has spaces
      let name = item.phone_number;
      if (item.driver) {
        id = item.driver.code.toString();
        name = item.driver.name;
      }
      const messagePreview: SmsChatMessagePreviewUnitProps = {
        id,
        name,
        datetime: item.last_message.created_at,
        message: item.last_message.content,
        isRead: item.is_read,
      };
      return messagePreview;
    });
  });

  const messages = computed<SmsChatMessageFullUnitProps[]>(() => {
    if (!data.value.messageHistoryData) return [];
    return data.value.messageHistoryData.results
      .map((item) => {
        let icon = 'mdi-check';
        if (item.status === 'pending') {
          icon = 'mdi-clock-outline';
        } else if (item.status === 'accepted') {
          icon = 'mdi-clock-outline';
        } else if (item.status === 'delivered') {
          icon = 'mdi-check';
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (item.status === 'failed') {
          icon = 'mdi-alert-outline';
        }
        if (item.is_read) {
          icon = 'mdi-check-all';
        }
        const message: SmsChatMessageFullUnitProps = {
          id: item.id,
          name: item.sender_name,
          datetime: item.created_at,
          message: item.content,
          icon,
          isOwn: item.direction === 'outbound',
        };
        return message;
      })
      .reverse();
  });

  const currentConversationItem = computed<
    | ReadonlyDeep<openapi.GetSmsMessagesConversations_200['results'][0]>
    | undefined
  >(() => {
    if (!query.value.recipient) return;
    if (!data.value.conversationsData) return;
    let item: openapi.GetSmsMessagesConversations_200['results'][0] | undefined;
    if (query.value.recipient.toString().length > 4) {
      item = data.value.conversationsData.results.find((item) => {
        return item.phone_number === query.value.recipient?.toString();
      });
    } else {
      item = data.value.conversationsData.results.find((item) => {
        return item.driver?.code === query.value.recipient;
      });
    }
    return item;
  });

  const details = computed<SmsChatDetailsSectionProps>(() => {
    if (!currentConversationItem.value) return {};
    return {
      name: recipientName.value,
      role: currentConversationItem.value.type,
      phone: currentConversationItem.value.phone_number,
      email: currentConversationItem.value.driver?.email || undefined,
      state: currentConversationItem.value.driver?.state || undefined,
      driverCode: currentConversationItem.value.driver?.code,
    };
  });

  const recipientName = computed(() => {
    if (!currentConversationItem.value) return;
    return (
      currentConversationItem.value.driver?.name ||
      currentConversationItem.value.phone_number
    );
  });

  const recipientDatetime = computed(() => {
    return currentConversationItem.value?.last_message.created_at;
  });

  const drivers = computed(() => {
    return staticStore.getDrivers.map((driver) => {
      return {
        title: driver.code + ', ' + driver.full_name,
        value: driver.code,
      };
    });
  });

  const offsiders = computed(() => {
    return staticStore.getOffsiders.map((offsider) => {
      return {
        title: [offsider.full_name, offsider.department]
          .filter(Boolean)
          .join(', '),
        value: offsider.id,
      };
    });
  });

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defSmsChatViewState));
    _smsChatViewStore.$reset();
  }

  function init(
    viewData: SmsChatViewDataComposable,
    routeQuery: SmsRouteQueryComposable,
  ) {
    _smsChatViewStore.setData(viewData);
    _smsChatViewStore.setQuery(routeQuery);
  }

  async function handleNewMessageChatDialogFormSubmit(
    formData: NewMessageChatDialogFormValidData,
  ): Promise<unknown> {
    if (!data.value.refreshSendData) return;
    if (formData.receiverType === 'driver') {
      await data.value.refreshSendData({
        driver:
          formData.driver != null ? formData.driver.toString() : undefined,
        offsider: formData.offsider ?? undefined,
        extra_number: formData.extraPhone,
        message: formData.message,
      });
    } else {
      await data.value.refreshSendData({
        phone_number: formData.phone,
        extra_number: formData.extraPhone,
        message: formData.message,
      });
    }
  }

  async function handleChatMainSendFormSubmit(
    event: SubmitEventPromise | null | undefined,
    formData: ChatMainSendFormValidData,
  ): Promise<unknown> {
    if (!data.value.refreshSendData) return;
    if (!currentConversationItem.value) return;
    const driverCode = currentConversationItem.value.driver?.code;
    await data.value.refreshSendData(
      driverCode != null
        ? { driver: driverCode.toString(), message: formData.message }
        : {
            phone_number: currentConversationItem.value.phone_number,
            message: formData.message,
          },
    );
    chatMainSendFormInitialData.value = { message: undefined };
    if (data.value.reload) data.value.reload();
  }

  function handleChatRosterSideBarFormSubmit(
    event: SubmitEventPromise | null | undefined,
    formData: ChatRosterSideBarFormValidData,
  ): void {
    state.search = formData.search;
  }

  function handleCreateNewMessageClick() {
    const dialogName: DialogName = 'newMessageChat';
    dialogStore
      .open(dialogName, { confirm: handleNewMessageChatDialogFormSubmit })
      .once(
        'closed',
        function (
          closeDetail: DefDialogState[typeof dialogName]['closeDetail'],
        ) {
          if (closeDetail === 'confirm') {
            if (data.value.reload) data.value.reload();
          }
        },
      );
  }

  watch(
    [() => data.value.conversationsData, () => state.search],
    function (newValues, oldValues) {
      if (!data.value.conversationsData?.results) {
        state.conversations = [];
        return;
      }
      if (!state.search) {
        state.conversations = data.value.conversationsData.results;
        return;
      }
      const search = state.search.toLowerCase();
      state.conversations = data.value.conversationsData.results.filter(
        (item) => {
          if (item.last_message.content.toLowerCase().includes(search)) {
            return true;
          }
          if (item.phone_number.toLowerCase().includes(search)) return true;
          if (!item.driver) return false;
          if (item.driver.code.toString().toLowerCase().includes(search)) {
            return true;
          }
          if (item.driver.name.toLowerCase().includes(search)) return true;
          if (item.driver.email?.toLowerCase().includes(search)) return true;
          if (item.driver.state?.toLowerCase().includes(search)) {
            return true;
          }
          if (item.driver.mobile_phone.toLowerCase().includes(search)) {
            return true;
          }
          return false;
        },
      );
    },
    { immediate: true },
  );

  watch(
    () => query.value.recipient,
    function () {
      chatMainSendFormInitialData.value = { message: undefined };
    },
  );

  watch(
    [() => query.value.recipient, () => data.value.conversationsData],
    async function (newValues, oldValues) {
      if (!query.value.recipient) return;
      if (!data.value.conversationsData) return;
      if (_isEqual(newValues, oldValues)) return;
      if (!currentConversationItem.value) return;
      const isRead = currentConversationItem.value.is_read;
      const driverCode = currentConversationItem.value.driver?.code;
      const phoneNumber = currentConversationItem.value.phone_number;
      if (!isRead) {
        if (data.value.refreshMarkReadData) {
          if (driverCode != null) {
            await data.value.refreshMarkReadData({
              driver: driverCode,
            });
          } else {
            await data.value.refreshMarkReadData({
              phone_number: phoneNumber,
            });
          }
        }
        if (data.value.refreshConversationsData) {
          await data.value.refreshConversationsData();
        }
      }
    },
    { immediate: true },
  );

  return {
    ...toRefs(state),
    data,
    query,
    newMessageChatDialogFormInitialData,
    chatMainSendFormInitialData,
    chatRosterSideBarFormInitialData,
    messagePreviews,
    messages,
    details,
    recipientName,
    recipientDatetime,
    offsiders,
    drivers,
    $reset,
    init,
    handleNewMessageChatDialogFormSubmit,
    handleChatMainSendFormSubmit,
    handleChatRosterSideBarFormSubmit,
    handleCreateNewMessageClick,
  };
}

const usePrivateSmsChatViewStore = defineStore(
  '_smsChatView',
  privateSmsChatViewStoreSetup,
);

export const useSmsChatViewStore = defineStore(
  'smsChatView',
  smsChatViewStoreSetup,
);
